import {
  ColDef,
  GetMainMenuItemsParams,
  GridOptions,
  MenuItemDef,
  RowClassParams,
  RowHeightParams,
} from 'ag-grid-enterprise';

import {
  defineMenuItem,
  LumAgGridEvent,
} from 'app/shared/utils/ag-grid.menu-item.utils';
import { EPicklistOderStatus } from 'v2/modules/picklist/interfaces/picklist.interface';
import { AgGridCustomRadioFilterComponent } from 'v2/modules/shared/components/ag-grid-custom-radio-filter/ag-grid-custom-radio-filter.component';
import { sidemenuItemTemplate } from 'v2/modules/shared/components/ag-grid-sidemenu/ag-grid-sidemenu.component';
import {
  IAgGridSideMenuItem,
  IAgGridSideMenuItemConfig,
} from 'v2/modules/shared/interfaces/ag-grid/ag-grid-sidemenu.interface';
import { SortDirections } from 'v2/modules/shared/interfaces/common.interface';

import { SalesOrderGroupColumnComponent } from '../components/sales-order-group-column/sales-order-group-column.component';
import {
  ESalesOrderStatusValue,
  ISalesOrderGroup,
} from '../interfaces/sales-order.interface';

const relations :string[] = [
  'picklist:id,status',
  'cellFormat',
  'channel:id',
  'channel.pushOrderIntegrationMapping:id,app_integration_account_id,channel_id',
  'channel.pushOrderIntegrationMapping.appIntegrationAccount',
  'channel.channelIntegrationMapping:id,app_integration_account_id,channel_id',
  'channel.pullOrderIntegrationMapping:id,app_integration_account_id,channel_id',
  'approval',
  'approval.approvalUsers',
  'trackingNumbers',
  'warehouseGroup:id,label,pick_and_pack_enabled',
  'salesOrderShipments|salesOrderShipments.warehouseGroup|salesOrderShipments.destinationOms',
];

const columns :string[] = [
  'id',
  'is_posted',
  'orderNumber',
  'source',
  'shipstationOrderId',
  'picking_status',
  'billTo',
  'channel_id',
  'orderDate',
  'doNotShipBefore',
  'orderStatus',
  'orderId',
  'orderTotal',
  'carrierCode',
  'serviceCode',
  'packageCode',
  'confirmation',
  'weight',
  'dimensions',
  'shipByDate',
  'customer_po_number',
  'warehouse_group_id',
];

class _SelectionRange {
  start = null;
  end = null;

  get max() {
    return Math.max(this.start, this.end);
  }
  get min() {
    return Math.min(this.start, this.end);
  }

  isComplete() {
    return this.start !== null && this.end !== null;
  }
  isSelecting() {
    return this.start !== null;
  }

  reset(start ?:number) {
    this.start = start ?? null;
    this.end = null;
  }
}

export interface ISalesOrderGridContext {
  selectionRange :_SelectionRange;
  tagsAreCollapsed :boolean;
}

const gridOptions :GridOptions = {
  suppressAutoSize: false,
  suppressCopyRowsToClipboard: true,
  components: {
    AgGridCustomRadioFilterComponent,
  },
  context: {
    selectionRange: new _SelectionRange(),
    tagsAreCollapsed: false,
  } as ISalesOrderGridContext,
  getMainMenuItems: (params :GetMainMenuItemsParams) => {
    // preserve built-in items by cloning the default items array as the base
    // for our menu
    const menuItems :(string | MenuItemDef)[] = params.defaultItems.slice();

    if (params.column.getColId() === fields.TAGS) {
      const event = params.context.tagsAreCollapsed
        ? LumAgGridEvent.COLUMN_TAGS_EXPAND
        : LumAgGridEvent.COLUMN_TAGS_COLLAPSE;

      menuItems.push('separator', defineMenuItem(event, params));
    }

    return menuItems;
  },
  getRowClass(params :RowClassParams) {
    return params.node.group
      ? 'lum-ag-row-group-row lum-ag-row-group-row--breakout'
      : '';
  },
  getRowHeight(params :RowHeightParams) {
    if (params.node.detail) {
      const detailTableRows = (params.node.data?.orderItems?.length || 1) + 1; // at least 1, plus 1 more for the header row

      const gridHeight = 35 * detailTableRows;
      const verticalMargin = 13; // .5rem pad top + .5rem pad bottom
      const extraHeight = 35; // some additional extra height for readability and in case of miscalculation

      return gridHeight + verticalMargin + extraHeight;
    }

    return params.node.group ? 35 : null;
  },
  groupDefaultExpanded: 0,
  groupDisplayType: 'groupRows',
  groupRowRenderer: SalesOrderGroupColumnComponent,
  groupRowRendererParams: {
    groups: [] as ISalesOrderGroup[], // updated by an API call
  },
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: false,
        },
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: '',
  },
};

// `label` is for the "group by >" menu
const groupByColumns :{ label :string; field :string }[] = [
  {
    field: 'channelName',
    label: 'Channel Name',
  },
  {
    field: 'itemName',
    label: 'Item Name',
  },
  {
    field: 'itemSku',
    label: 'Item SKU',
  },
  {
    field: 'shipToCountry',
    label: 'Ship To Country',
  },
  {
    field: 'totalQuantity',
    label: 'Total Quantity',
  },
  {
    field: 'numberOfLineItems',
    label: 'Number of Line Items',
  },
];

const defaultColDef :ColDef = {
  enablePivot: false,
  enableRowGroup: false,
  filter: true,
  filterParams: {
    filterOptions: ['equals'],
    defaultOption: 'equals',
    maxNumConditions: 1,
    debounceMs: 1000,
  },
  floatingFilter: true,
  resizable: true,
  sortable: true,
};

const fields = {
  ACTIONS: 'actions',
  AGE: 'age',
  APP_INTEGRATION_ACCOUNT_ID: 'appIntegrationAccountId',
  APPROVAL_STATUS: 'approvalStatus',
  BILL_TO_COMPANY: 'billTo.company',
  BILL_TO_NAME: 'billTo.name',
  CARRIER_CODE: 'carrierCode',
  CHANNEL_IDS: 'channelIds',
  CHECKBOX_SELECT_ALL: 'checkboxSelectAll',
  CUSTOM_FIELDS: 'customFields',
  CUSTOMER_NOTES: 'customerNotes',
  DO_NOT_SHIP_BEFORE_DATE: 'doNotShipBefore',
  INVOICE_ID: 'invoiceId',
  INVOICE_IDS: 'clientInvoices',
  INVOICE_PAYMENT_STATUS: 'invoicePaymentStatus',
  INTERNAL_NOTES: 'internalNotes',
  IS_POSTED: 'is_posted',
  MASTER_ROW: 'masterRow',
  OMS: 'oms',
  OMS_STATUS: 'oms_status',
  ORDER_DATE: 'orderDate',
  ORDER_ID: 'orderId',
  ORDER_KEY: 'orderKey',
  ORDER_NUMBER: 'orderNumber',
  ORDER_STATUS: 'orderStatus',
  ORDER_STATUSES: 'orderStatuses',
  ORDER_TOTAL: 'orderTotal',
  ORDER_VERIFIED: 'orderVerified',
  PICKING_STATUS: 'picking_status',
  PRODUCT_NAME: 'productName',
  QUANTITY: 'quantity',
  RECIPIENT_NAME: 'recipientName',
  REQUESTED_SHIP_DATE: 'shipByDate',
  REQUESTED_SHIPPING_SERVICE: 'requestedShippingService',
  SHIPPING_ADDRESS: 'shippingAddress',
  SHIPPING_COMPANY: 'shippingCompany',
  SHIPPING_NAME: 'shippingName',
  SHIPMENT_TRACKING_NUMBERS: 'shipmentTrackingNumbers',
  SHIP_DATE: 'shipDate',
  SKU: 'sku',
  SOURCE: 'source',
  STARRED_DESCRIPTION: 'starredDescription',
  SALES_ORDER_SHIPMENTS_STATUS: 'salesOrderShipments',
  TAGS: 'tagIds',
  TYPE: 'type',
  VERIFIED_DATE: 'verifiedDate',
  WAREHOUSE_GROUP: 'warehouseGroup.id',
  INVOICE_STATUS: 'invoiceStatus',
  CUSTOMER_PO_NUMBER: 'customerPoNumber',
};

const masterDetailRowFields = {
  INTERNAL_SKU: 'rfq.internal_sku',
  PRODUCT_NAME: 'rfq.product_name',
  ESTIMATED_ARRIVAL_DATE: 'estimatedArrivalDate',
  LOCATION: 'location',
  LOT: 'lot.lot_number',
  ORDERED_QTY: 'orderedQuantity',
  UOM: 'uom',
  AVAILABLE_QTY: 'availableQuantity',
};

const pageSizeOptions :number[] = [5, 10, 25, 100];

const defaultQueryParams :{ [key :string] :any } = {
  relations: relations.join('|'),
  sortField: fields.ORDER_DATE,
  sortDirection: SortDirections.DESC,
  limit: '100',
  excludeDeleted: true,
  page: 1,
  orderStatuses: EPicklistOderStatus.PENDING,
};

const defaultGroupQueryParams = {
  limit: '10',
  page: '1',
};

const sideMenuConfig :IAgGridSideMenuItemConfig = {
  renderHtml: (item :IAgGridSideMenuItem) => {
    let itemTextColor = '';
    let itemBadgeColor = '';

    switch (item.name) {
      case ESalesOrderStatusValue.PENDING:
        itemTextColor = 'text-yellow-500';
        itemBadgeColor = 'bg-yellow-200 text-yellow-800';
        break;

      case ESalesOrderStatusValue.AWAITING_SHIPMENT:
        itemTextColor = 'text-yellow-300';
        itemBadgeColor = 'bg-yellow-100 text-yellow-800';
        break;

      case ESalesOrderStatusValue.CANCELLED:
        itemTextColor = 'text-red-500';
        itemBadgeColor = 'bg-red-200 text-red-800';
        break;

      case ESalesOrderStatusValue.ON_HOLD:
        itemTextColor = 'text-blue-500';
        itemBadgeColor = 'bg-blue-200 text-blue-800';
        break;

      case ESalesOrderStatusValue.PARTIALLY_SHIPPED:
        itemTextColor = 'text-green-300';
        itemBadgeColor = 'bg-green-100 text-green-800';
        break;

      case ESalesOrderStatusValue.SHIPPED:
        itemTextColor = 'text-success';
        itemBadgeColor = 'bg-green-200 text-green-800';
        break;

      default:
        itemTextColor = 'text-cloud-500';
        itemBadgeColor = 'bg-cloud-200 text-cloud-800';
        break;
    }

    return sidemenuItemTemplate({
      badgeColorClass: itemBadgeColor,
      badgeLabel: item.data?.orderStatusCount,
      colorClass: itemTextColor,
      label: item.label,
    });
  },
};

const onDemandServerSideRelations :{ [key :string] :string } = {
  [fields.ORDER_STATUSES]: 'latestShipstationCreateOrderStatus',
  [fields.MASTER_ROW]: 'orderItems',
  [fields.SKU]: 'orderItems',
  [fields.PRODUCT_NAME]: 'orderItems',
  [fields.QUANTITY]: 'orderItems',
  [fields.ORDER_ID]: 'flaggedTag',
  [fields.INVOICE_ID]:
    'clientInvoice:id,payment_status,paid_amount,net_total_amount',
  [fields.INVOICE_IDS]: 'clientInvoices:id',
  [fields.CHANNEL_IDS]: 'channel',
  [fields.TAGS]: 'tags',
  [fields.SHIPPING_COMPANY]:
    'clientInvoice:id,client_id|clientInvoice.client:id,business_name',
  [fields.SHIPMENT_TRACKING_NUMBERS]: 'shipstationShipments',
  [fields.ORDER_VERIFIED]:
    'picklistOrder:id,verified_by|picklistOrder.verifiedBy:id,firstname,lastname',
  [fields.VERIFIED_DATE]: 'picklistOrder:id,verified_at',
  [fields.INVOICE_PAYMENT_STATUS]: 'clientInvoice:id,payment_status',
  [fields.CUSTOM_FIELDS]: 'customFieldValues',
};
const onDemandServerSideColumns :{ [key :string] :string } = {
  [fields.INTERNAL_NOTES]: 'internalNotes',
  [fields.PICKING_STATUS]: 'picking_status',
  [fields.SHIP_DATE]: 'shipDate',
  [fields.RECIPIENT_NAME]: 'shipTo',
  [fields.ORDER_STATUSES]: 'orderStatus',
  [fields.STARRED_DESCRIPTION]: 'starred_description',
  [fields.CUSTOMER_NOTES]: 'customerNotes',
  [fields.ORDER_KEY]: 'orderKey',
  [fields.SHIPPING_NAME]: 'shipTo',
  [fields.SHIPPING_ADDRESS]: 'shipTo',
  [fields.TYPE]: 'type',
  [fields.CARRIER_CODE]: 'carrierCode',
  [fields.OMS_STATUS]: 'oms_status',
  [fields.REQUESTED_SHIPPING_SERVICE]: 'requestedShippingService',
};

export const SalesOrderAgTableConfig = {
  relations,
  onDemandServerSideRelations,
  gridOptions,
  defaultColDef,
  pageSizeOptions,
  fields,
  defaultQueryParams,
  groupByColumns,
  defaultGroupQueryParams,
  masterDetailRowFields,
  sideMenuConfig,
  columns,
  onDemandServerSideColumns,
};
